/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { pathConstants } from '../../constants';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userActions } from '../../redux/actions';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(90deg, rgba(0, 123, 255, 0.75) 0%, rgba(0, 191, 255, 0.75) 100%),
              url('https://images.unsplash.com/photo-1427504494785-cdec5f80e9cf?w=1920&q=80') center/cover;
  background-attachment: fixed;
  background-blend-mode: multiply;
  padding: 20px;
  animation: fadeIn 0.5s ease-in;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 20%, rgba(0, 123, 255, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(0, 191, 255, 0.15) 0%, transparent 50%);
    pointer-events: none;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 20px 28px 24px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.6s ease-out;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px 20px 20px;
    max-width: 100%;
    border-radius: 16px;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 8px;
  margin-top: -8px;
  animation: float 3s ease-in-out infinite;

  img {
    width: 200px;
    height: auto;
    max-width: 100%;
    filter: drop-shadow(0 10px 20px rgba(37, 99, 235, 0.3));
  }

  @media (max-width: 768px) {
    margin-bottom: 6px;
    margin-top: -4px;
    
    img {
      width: 160px;
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  text-align: center;
  margin: 4px 0 0 0;

  @media (max-width: 768px) {
    font-size: 20px;
    margin: 2px 0 0 0;
  }
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: #64748b;
  text-align: center;
  margin: 2px 0 16px 0;

  @media (max-width: 768px) {
    font-size: 11px;
    margin: 2px 0 14px 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #334155;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 10px 12px;
  font-size: 14px;
  border: 2px solid ${props => props.$hasError ? '#ef4444' : '#e2e8f0'};
  border-radius: 10px;
  transition: all 0.2s ease;
  outline: none;

  @media (max-width: 768px) {
    padding: 9px 11px;
    font-size: 13px;
  }

  &:focus {
    border-color: ${props => props.$hasError ? '#ef4444' : '#2563eb'};
    box-shadow: 0 0 0 4px ${props => props.$hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(37, 99, 235, 0.1)'};
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #ef4444;
  margin-top: 2px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'google' }>`
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
  }

  ${props => props.$variant === 'google' ? `
    background: white;
    color: #1e293b;
    border: 2px solid #e2e8f0;
    
    &:hover {
      border-color: #cbd5e1;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  ` : `
    background: linear-gradient(90deg, #007bff 0%, #00bfff 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 123, 255, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
  color: #94a3b8;
  font-size: 12px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: 12px;
  margin-bottom: 0;
  font-size: 13px;
  color: #64748b;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-top: 10px;
  }
`;

const StyledLink = styled(Link)`
  color: #2563eb;
  font-weight: 600;
  text-decoration: none;
  margin-left: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #3b82f6;
    text-decoration: underline;
  }
`;

const ContactInfo = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  font-size: 11px;
  color: #64748b;
  text-align: left;
  z-index: 10;
  background: white;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    font-size: 10px;
    bottom: 16px;
    left: 16px;
    padding: 10px 12px;
    border-radius: 10px;
  }

  strong {
    color: #334155;
    display: block;
    margin-bottom: 4px;
  }

  a {
    color: #2563eb;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4"/>
    <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853"/>
    <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05"/>
    <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335"/>
  </svg>
);

const LoginPage: React.FC = () => {
  const [inputs, setInputs] = useState({
    userName: '',
    password: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { userName, password } = inputs;
  const loggingIn = useAppSelector((state: any) => state.authentication.loggingIn);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(userActions.logout());
  }, [dispatch]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setInputs((input) => ({ ...input, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setErrorMsg('');
    if (userName && password) {
      try {
        await dispatch(userActions.login(userName, password));
        // Lấy lại userInfo từ localStorage sau khi login
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (userInfo && userInfo.role !== undefined) {
          if (userInfo.role === 0 || userInfo.role === 1) {
            navigate(pathConstants.LESSON_DIARY_MANAGE);
          } else if (userInfo.role === 2) {
            navigate(pathConstants.STUDENT_PAGE);
          } else {
            navigate(pathConstants.ROOT);
          }
        } else {
          setErrorMsg('Sai tên đăng nhập hoặc mật khẩu');
        }
      } catch (error: any) {
        setErrorMsg('Sai tên đăng nhập hoặc mật khẩu');
      }
    }
  };

  const handleGoogleLogin = () => {
    // Dynamically determine backend URL based on current location
    const host = window.location.host;
    
    let backendUrl = 'http://localhost:5001'; // default for local dev
    
    if (host.includes('tungkt.com')) {
      backendUrl = 'https://tungkt.com';
    }
    
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  return (
    <PageContainer>
      <LoginCard>
        <Logo>
          <img src="/kt-english-logo.png" alt="KT English Logo" />
        </Logo>
        <Title>Chào mừng trở lại!</Title>
        <Subtitle>Đăng nhập để tiếp tục học tập</Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Tên đăng nhập</Label>
            <Input
              type="text"
              name="userName"
              value={userName}
              onChange={handleChange}
              placeholder="Nhập tên đăng nhập"
              $hasError={submitted && !userName}
            />
            {submitted && !userName && <ErrorText>Vui lòng nhập tên đăng nhập</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>Mật khẩu</Label>
            <div style={{position:'relative', width:'100%'}}>
              <Input
                type={showPassword ? 'password' : 'text'}
                name="password"
                value={password}
                onChange={handleChange}
                placeholder="Nhập mật khẩu"
                $hasError={submitted && !password}
                style={{width:'100%',paddingRight:'40px',boxSizing:'border-box'}}
              />
              <span
                style={{position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',cursor:'pointer',fontSize:'20px',color:'#64748b',background:'transparent',height:'24px',display:'flex',alignItems:'center'}}
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Hiện mật khẩu' : 'Ẩn mật khẩu'}
              >
                {showPassword ? '👁' : '👁️'}
              </span>
            </div>
            {submitted && !password && <ErrorText>Vui lòng nhập mật khẩu</ErrorText>}
          </FormGroup>

          {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
          <Button type="submit" disabled={loggingIn}>
            {loggingIn && <Spinner />}
            Đăng nhập
          </Button>
        </Form>

        <Divider>hoặc</Divider>

        <Button $variant="google" type="button" onClick={handleGoogleLogin}>
          <GoogleIcon />
          Đăng nhập với Google
        </Button>

        <LinkContainer>
          Chưa có tài khoản?
          <StyledLink to={pathConstants.SIGNUP}>Đăng ký ngay</StyledLink>
        </LinkContainer>
      </LoginCard>

      <ContactInfo>
        <strong>💬 Cần hỗ trợ?</strong>
        📞 0918334306 (Zalo)<br />
        📘 <a href="https://www.facebook.com/duongkhactung1610" target="_blank" rel="noopener noreferrer">
          Facebook: TungDK
        </a>
      </ContactInfo>
    </PageContainer>
  );
};

export default LoginPage;
